import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';

import { Permission } from '@/api/permissions';
import { useApproveRoleRequest, useRejectRoleRequest } from '@/api/role-requests/mutations';
import { _getRoleRequests } from '@/api/role-requests/queries';
import type { EntityRoleRequest } from '@/api/role-requests/types';
import { theme, Typography } from '@/shared/design-system';
import { useCan } from '@/shared/modules/permissions';
import { useSelectedUserConfigSlice } from '@/stores/selectors';

import { RoleRequestsList } from './role-requests-list';
import { RoleRequestStatus, type RoleRequestView } from './types';

const actorLabel = (actor: EntityRoleRequest['initiator']) =>
  actor.name || actor.email || 'Former user';

const toEntityRequestView = (request: EntityRoleRequest): RoleRequestView => ({
  ...request,
  initiatorLabel: actorLabel(request.initiator),
  deciderLabel: request.decider ? actorLabel(request.decider) : null,
  targetLabel: request.target ? actorLabel(request.target) : null,
});

export const EntityRoleRequestsTab = () => {
  const { selectedEntityId } = useSelectedUserConfigSlice();

  return <EntityRoleRequestsPanel entityId={selectedEntityId} />;
};

export const EntityRoleRequestsPanel = ({ entityId }: { entityId?: string | null }) => {
  const [status, setStatus] = useState<RoleRequestStatus>(RoleRequestStatus.Pending);
  const canApprove = useCan(Permission.EntityApproveRoleRequests);
  const canView = useCan(Permission.EntityViewRoles) || canApprove;
  const requests = useQuery(_getRoleRequests(canView ? entityId : null, status));
  const pending = useQuery(_getRoleRequests(canView ? entityId : null, RoleRequestStatus.Pending));
  const approve = useApproveRoleRequest({ entityId });
  const reject = useRejectRoleRequest({ entityId });

  const requestViews = (requests.data?.requests ?? []).map(toEntityRequestView);

  if (!entityId) {
    return (
      <Typography variant="body3" color={theme.colors.text.secondary}>
        Switch to an entity to review its role requests.
      </Typography>
    );
  }

  if (!canView) {
    return (
      <Typography variant="body3" color={theme.colors.text.secondary}>
        You don&apos;t have permission to view this entity&apos;s role requests.
      </Typography>
    );
  }

  return (
    <RoleRequestsList
      requests={requestViews}
      status={status}
      pendingCount={pending.data?.requests.length ?? 0}
      canApprove={canApprove}
      isLoading={requests.isPending}
      isError={requests.isError}
      isDecisionPending={approve.isPending || reject.isPending}
      onStatusChange={setStatus}
      onApprove={(requestId) => approve.mutate(requestId)}
      onReject={(requestId, reason) => reject.mutate({ requestId, reason })}
      onRetry={() => requests.refetch()}
    />
  );
};
